import type { Permission } from '../domain/types';
import { API_PATHS } from './paths';

export type WriteEndpoint = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** 契約書と同じ表記(`{id}` がパスパラメータ)。 */
  path: string;
  permission: Permission;
};

/**
 * 権限が要る API(書き込みと、アカウント一覧の読み取り)の唯一の対応表。モックサーバー(handlers.ts)、契約書(scripts の contractDoc)、
 * 権限の網羅テストがすべてこれを使う。ここを直せば 3 か所が同時に変わる。
 */
export const WRITE_ENDPOINTS: readonly WriteEndpoint[] = [
  { method: 'POST', path: API_PATHS.tasks, permission: 'tasks:write' },
  { method: 'PUT', path: `${API_PATHS.tasks}/{id}`, permission: 'tasks:write' },
  { method: 'DELETE', path: `${API_PATHS.tasks}/{id}`, permission: 'tasks:write' },
  { method: 'PUT', path: `${API_PATHS.schedule}/rows/{taskId}`, permission: 'schedule:write' },
  { method: 'PUT', path: API_PATHS.scheduleMilestones, permission: 'schedule:write' },
  { method: 'PUT', path: API_PATHS.scheduleWorkingGroups, permission: 'schedule:write' },
  { method: 'POST', path: API_PATHS.cases, permission: 'cases:write' },
  { method: 'PUT', path: `${API_PATHS.cases}/{id}`, permission: 'cases:write' },
  { method: 'DELETE', path: `${API_PATHS.cases}/{id}`, permission: 'cases:write' },
  { method: 'POST', path: API_PATHS.staff, permission: 'staff:write' },
  { method: 'PUT', path: `${API_PATHS.staff}/{id}`, permission: 'staff:write' },
  { method: 'DELETE', path: `${API_PATHS.staff}/{id}`, permission: 'staff:write' },
  {
    method: 'PUT',
    path: `${API_PATHS.staff}/{staffId}/allocations/{caseId}`,
    permission: 'allocations:write',
  },
  {
    method: 'DELETE',
    path: `${API_PATHS.staff}/{staffId}/allocations/{caseId}`,
    permission: 'allocations:write',
  },
  // アカウント管理は一覧の読み取りも accounts:write が要る(名簿は管理者だけに見せる)
  { method: 'GET', path: API_PATHS.accounts, permission: 'accounts:write' },
  { method: 'POST', path: API_PATHS.accounts, permission: 'accounts:write' },
  { method: 'PUT', path: `${API_PATHS.accounts}/{id}`, permission: 'accounts:write' },
  { method: 'PUT', path: `${API_PATHS.accounts}/{id}/password`, permission: 'accounts:write' },
  { method: 'DELETE', path: `${API_PATHS.accounts}/{id}`, permission: 'accounts:write' },
];

/** その書き込み API に必要な権限。表にない組み合わせは起動時に気づけるよう例外にする。 */
export const writePermission = (method: WriteEndpoint['method'], path: string): Permission => {
  const found = WRITE_ENDPOINTS.find((e) => e.method === method && e.path === path);
  if (found === undefined) throw new Error(`WRITE_ENDPOINTS に無い書き込み API: ${method} ${path}`);
  return found.permission;
};

/** 契約書の表記 `{id}` を MSW のパス表記 `:id` にし、先頭にオリジンのワイルドカードを付ける。 */
export const toMswPath = (path: string): string => `*${path.replace(/\{(\w+)\}/g, ':$1')}`;
